import { App, View } from "@pimo/pimo-app-builder";
import {
  DerivePMTableProps,
  generateTableHeaderEntriesWithSorting,
  PMTableEventNames,
  PMTableEventPayload,
  PMTableSortEventPayload,
  SortDirection,
} from "@pimo/pimo-components";
import { DragAndDropLayout } from "@pimo/pimo-components/src/lib/layouts/drag-and-drop-layout/drag-and-drop-layout";
import ChipCell from "@pimo/pimo-components/src/lib/molecules/chip-cell/chip-cell";
import TextCardCell from "@pimo/pimo-components/src/lib/molecules/text-card-cell/text-card-cell";
import PMTable from "@pimo/pimo-components/src/lib/pm-components/pm-table/pm-table";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import React from "react";
import { APP_RECORD_TYPES, ReportingItem } from "vmd-types";

import { TestAppState } from "../../../app";
import { DynamicTablePagination } from "../../../components/dynamic-table/dynamic-table-component";
import { RECORD_TYPE_BADGE_COLORS } from "../../../constants/colors";
import { createDynamicTableState } from "../../../helpers/dynamic-table";
import {
  criticalityLevelSet,
  getAllReportingItems,
} from "../../../helpers/pages-helpers/dashboard-plugin-helpers";
import {
  ChannelStats,
  ChannelStatsSortData,
  sortChannelStats,
} from "../../../utils/sort-table-data";

const dashboardChannelSortKeys = new Set<ChannelStatsSortData["key"]>([
  "channel",
  "channelLabel",
  "businessApps",
  "criticalSystems",
]);

const getChannelStatsData = (
  items: ReportingItem[],
  channelDefinition?: { id: string; label: string }[]
): ChannelStats[] => {
  const channelMap = new Map<string, ChannelStats>();
  const channelLabelById = new Map(
    (channelDefinition ?? []).map((ch) => [ch.id, ch.label])
  );

  for (const item of items) {
    const channelIds = item.distributionChannel ?? [];
    if (!channelIds.length) {
      continue;
    }

    for (const channelId of channelIds) {
      if (!channelMap.has(channelId)) {
        channelMap.set(channelId, {
          channelId,
          channelLabel: channelLabelById.get(channelId) ?? channelId,
          totalSystems: 0,
          businessApps: 0,
          itServices: 0,
          criticalSystems: 0,
        });
      }

      const stats = channelMap.get(channelId)!;
      stats.totalSystems += 1;

      if (
        item.basicItem?.recordType === APP_RECORD_TYPES.BUSINESS_APPLICATION
      ) {
        stats.businessApps += 1;
      } else if (item.basicItem?.recordType === APP_RECORD_TYPES.IT_SERVICE) {
        stats.itServices += 1;
      }

      if (
        item.basicItem?.criticality &&
        criticalityLevelSet.has(item.basicItem.criticality)
      ) {
        stats.criticalSystems += 1;
      }
    }
  }

  return Array.from(channelMap.values()).sort(
    (a, b) => b.totalSystems - a.totalSystems
  );
};

export function addChannelsTableCard(
  view: View<TestAppState, DragAndDropLayout>,
  app: App<TestAppState>
): void {
  const channelsTableClassComponent = new PMTable([
    { component: TextCardCell },
    { component: ChipCell },
    { component: ChipCell },
  ]);

  const chipProps = {
    sx: {
      backgroundColor: RECORD_TYPE_BADGE_COLORS.businessApplication,
      px: 2,
      borderRadius: "8px",
    },
  };

  const channelsTableComponent = view.addComponent<
    DerivePMTableProps<typeof channelsTableClassComponent>,
    PMTableEventNames,
    PMTableEventPayload
  >({
    component: channelsTableClassComponent,
    layoutProps: {
      componentId: "channelsTableDashboardComponent",
      labels: ["table", "channels"],
      description: "Shows all distribution channels with system counts.",
      title: "Systems per Distribution Channel",
    },
  });

  channelsTableComponent.on("sort", (event) => {
    if (!event?.payload || !app) {
      return;
    }

    const payload = event.payload as PMTableSortEventPayload<
      ChannelStatsSortData["key"]
    >;
    const sortKey = payload?.sort?.key;

    if (!sortKey || !dashboardChannelSortKeys.has(sortKey)) {
      return;
    }

    const state = app.getAppState();
    let direction: SortDirection = "ASC";

    if (
      state.dashboardPluginSortData?.key === sortKey &&
      state.dashboardPluginSortData?.direction === "ASC"
    ) {
      direction = "DESC";
    }

    app.patchAppState({
      dashboardPluginSortData: {
        key: sortKey,
        direction,
      },
    });
  });

  channelsTableComponent.mapState(
    ({
      dashboard,
      settings,
      language,
      dashboardTablePagination,
      dashboardPluginSortData,
    }) => {
      const allItems = getAllReportingItems(dashboard);
      const stats = getChannelStatsData(
        allItems,
        settings?.configuration?.distributionChannelDefinition
      );
      const effectiveSortData: ChannelStatsSortData =
        dashboardPluginSortData ??
        ({
          key: "businessApps",
          direction: "DESC",
        } as ChannelStatsSortData);
      const sortedStats = sortChannelStats([...stats], effectiveSortData);

      const tableHeaderEntries = generateTableHeaderEntriesWithSorting<{
        channel: string;
        channelLabel: string;
        totalSystems: number;
        businessApps: number;
        itServices: number;
        criticalSystems: number;
      }>({
        entries: [
          {
            label:
              getLocalizedMessage(language || "en", "distributionChannel") ||
              "Distribution Channel",
            fieldName: "channel",
            isSortable: true,
          },

          {
            label:
              getLocalizedMessage(language || "en", "numberOfBusinessApps") ||
              "# DC Business Apps",
            fieldName: "businessApps",
            isSortable: true,
          },
          {
            label:
              getLocalizedMessage(
                language || "en",
                "numberOfCriticalSystems"
              ) || "# Critical Systems",
            fieldName: "criticalSystems",
            isSortable: true,
          },
        ],
        tableSortData: effectiveSortData,
      });

      const dynamicTableState = createDynamicTableState({
        app,
        paginationPrefix: "dashboardChannels",
        persistedPagination: dashboardTablePagination,
        onPatchPagination: (pagination) => {
          app?.patchAppState({
            dashboardTablePagination: pagination,
          });
        },
        items: sortedStats,
      });

      const paginatedStats = dynamicTableState.visibleItems;
      const paginationController = dynamicTableState.paginationController;

      return {
        title: "Systems per Distribution Channel",
        icon: "TableChart",
        showInfoIcon: false,
        showMenuIcon: false,
        bottomRightSlotOverride: React.createElement(DynamicTablePagination, {
          paginationController,
          totalItems: sortedStats.length,
        }),
        tableHeaderEntries,
        data: paginatedStats.map((stat) => ({
          columnProps: [
            { body: stat.channelLabel },
            {
              body: stat.businessApps.toString(),
              chipProps,
            },

            {
              body: stat.criticalSystems.toString(),
              chipProps,
            },
          ],
          rowProps: {
            cardProps: { sx: { cursor: "pointer" } },
          },
        })),
      };
    }
  );
}
